import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {AuthToken, ILoggedInPatient, LogoutReason} from '../common/common.types';
import { AuthTokenRefresherService } from './auth-token-refresher.service';
import {LogManager} from "../../common/log/log.manager";
import {AuthProviderProxy} from "./auth-provider.proxy";
import {Router} from "@angular/router";
import {LogCat} from "../../common/log/log.categories";


@Injectable({
  providedIn: 'root'
})
export class AuthProviderService implements OnDestroy {

  private readonly currentTokenSubject: BehaviorSubject<AuthToken> = new BehaviorSubject<AuthToken>(undefined);
  private readonly currentPatientSubject: BehaviorSubject<ILoggedInPatient> = new BehaviorSubject<ILoggedInPatient>(undefined);

  public readonly loginWatcher$: Observable<AuthToken>;


  constructor(private tokenRefresher: AuthTokenRefresherService,
              private authProviderProxy: AuthProviderProxy,
              private router: Router) {
    this.loginWatcher$ = this.currentTokenSubject.asObservable();
  }

  ngOnDestroy(): void { }

  logout(reason: LogoutReason): void {
    this.router.navigate(['/login']).then(() => {
      if (this.currentUserToken) {
        const token = this.currentUserToken.access_token;
        const refreshToken = this.currentUserToken.refresh_token;
        this.tokenRefresher.invalidateRefreshToken(refreshToken).subscribe(() => {
          LogManager.debug(LogCat.Login.TokenRefresh, "Invalidated refresh token for logged out user");
        }, (err) => {
          LogManager.error(LogCat.Login.TokenRefresh, "Failed to invalidate refresh token for logged out user", err);
        });
        this.tokenRefresher.Stop();
        this.setPatient(null);
        this.setToken(null);
        this.authProviderProxy.logout(reason, token).subscribe();
      }
    });
  }

  public get currentUserToken(): AuthToken {
    return this.currentTokenSubject.value != null ? this.currentTokenSubject.value : null;
  }

  public get currentPatient(): ILoggedInPatient {
    return this.currentPatientSubject.value;
  }

  public isAuthenticated(): boolean {
    return !(this.currentTokenSubject.value == null || this.currentPatientSubject.value == null);
  }

  /* set auth token and return subscription to logged in patient */
  public setCredentials(token: AuthToken): Subject<ILoggedInPatient> {
    this.setToken((token));
    this.startRefresher(token);
    const subjPatient = new Subject<ILoggedInPatient>();
    subjPatient.subscribe((patient) => {
        this.setPatient(patient);
        subjPatient.complete();
    });
    return  subjPatient;
  }

  public setAccountPreference(key: number) {
    const patient = this.currentPatient;
    patient.accountPreferences.defaultLanguageKey = key;
    this.setPatient(patient);
  }

  private startRefresher(token: AuthToken): void {
    if (token.refresh_token) {
      this.tokenRefresher.StartRefresh(token).subscribe((newToken) => {
        this.setToken((newToken));
      });
    }
  }

  private setToken(token: AuthToken): void {
    if (token == null) {
      this.currentTokenSubject.next(null);
    } else {
      this.currentTokenSubject.next(token);
      LogManager.setCredentials(token.access_token);
    }
  }

  private setPatient(patient: ILoggedInPatient): void {
    this.currentPatientSubject.next(patient);
  }
}

